import { Alert, Button } from "../../components";
import { CheckFirmwareForm, ValidatedInput } from "../components";
import { Suspense, useEffect, useState } from "react";
import {
  assignLicense,
  getError,
  getLicenseFormState,
  getSelectedDeviceData,
  initLicenseForm,
} from "../../redux/voiceControl-slice";
import {
  getErrorMsg,
  isValid,
  validateForm,
} from "../../services/validation-service";
import { useDispatch, useSelector } from "react-redux";
import cn from "classnames";
import { deviceInfoMessages } from "../../constants/voice";
import { form } from "../DeviceSettings.module.scss";
import { licenseAssignationValidation } from "../../shared/validation";
import { loadTestComponent } from "../../utils";
import sharedStyles from "../../shared.module.scss";
import { useAbortableAsyncThunk } from "../../hooks";
import { useFormatMessage } from "../../i18n";
import { useFormik } from "formik";

const errorMessages = {
  BAD_REQUEST: "error_license",
  DEVICE_ACCESS_DENIED: "error_device_access_denied",
  NOT_FOUND: "error_device_not_found",
  INVALID_LICENSE: "error_invalid_license",
  GATEWAY_TIMEOUT: "error_device_time_out",
  SERVER_ERROR: "something_went_wrong",
};

export function AssignLicenseForm({ isLicensePage }) {
  const [pristine, setPristine] = useState(true);
  const [showLicenseAssignedMessage, setShowLicenseAssignedMessage] =
    useState(false);
  const tr = useFormatMessage();
  const dispatch = useDispatch();
  const deviceSelected = useSelector(getSelectedDeviceData);
  const error = useSelector(getError);
  const isDeviceSelected = deviceSelected.status !== "unselected";
  const { dispatchWithAbort } = useAbortableAsyncThunk(
    assignLicense,
    deviceSelected.cloudId
  );
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      licenseKey: "",
      cloudId: isDeviceSelected ? deviceSelected.cloudId : "",
    },
    onSubmit: async (values) => {
      setPristine(false);
      if (isValid(validationErrors)) {
        const finalValues = {
          ...values,
          licenseKey: values.licenseKey.toUpperCase(),
        };
        const data = await dispatchWithAbort(finalValues);

        if (isLicensePage && data.meta.requestStatus === "fulfilled") {
          setShowLicenseAssignedMessage(true);
        }
      }
    },
  });

  useEffect(() => {
    setPristine(true);
    dispatch(initLicenseForm());

    return () => {
      dispatch(initLicenseForm());
    };
  }, [dispatch, formik.values.cloudId]);

  const { assignLicenseInProgress, isFirmwareVoiceCompatible } =
    useSelector(getLicenseFormState);

  const validationErrors = validateForm(
    { form: formik.values },
    licenseAssignationValidation
  );

  const errorMessage = tr(
    errorMessages[error?.type] || errorMessages.SERVER_ERROR
  );

  const licenseKeyErrorMessage = getErrorMsg(validationErrors, "licenseKey");

  const TestingWrapper = loadTestComponent(
    () => import("../../components/test/TestingWrapper")
  );

  const VoiceControlTestErrorButtons = loadTestComponent(
    () => import("../../components/test/VoiceControlTestErrorButtons")
  );

  return (
    <form
      onSubmit={formik.handleSubmit}
      className={cn(sharedStyles.form, form)}
    >
      {!isLicensePage && (
        <h4 className={cn(sharedStyles.title, sharedStyles.pageTitle)}>
          {tr("assign_license_title")}
        </h4>
      )}
      {isFirmwareVoiceCompatible ? (
        <>
          {isLicensePage && (
            <span
              className={cn(sharedStyles.subtitle, sharedStyles.mainText)}
            >{`${tr("assign_license_to")}: ${formik.values.cloudId}`}</span>
          )}
          <ValidatedInput
            label={tr("license")}
            id="license-input"
            name="licenseKey"
            className={sharedStyles.uppercase}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.licenseKey}
            errors={licenseKeyErrorMessage}
            pristine={pristine}
          />
          {showLicenseAssignedMessage && (
            <Alert severity={"success"}>
              {tr(deviceInfoMessages.licenseAssigned)}
            </Alert>
          )}
          {error && <Alert severity={"error"}>{errorMessage}</Alert>}
          <div className={cn(sharedStyles.btnGroup)}>
            <Button
              loading={assignLicenseInProgress}
              type="submit"
              title={tr("assign_license")}
              disabled={!isFirmwareVoiceCompatible || assignLicenseInProgress}
              variant="primary"
            />
          </div>
        </>
      ) : (
        <CheckFirmwareForm
          formik={formik}
          isDeviceSelected={isDeviceSelected}
        />
      )}
      {isFirmwareVoiceCompatible && (
        <Suspense fallback={<></>}>
          <TestingWrapper>
            <VoiceControlTestErrorButtons
              errors={Object.keys(errorMessages)}
              global={true}
            />
          </TestingWrapper>
        </Suspense>
      )}
    </form>
  );
}
